import { useState, useEffect, useCallback } from 'react'
import { APIProvider } from '@vis.gl/react-google-maps'

import { config } from './config'
import Header from './components/header'
import Search from './components/search'
import ShowMap from './components/map'
import ShowList from './components/list'
import Switch from './components/switch'
import Modal from './components/modal'
import { ErrorBoundary } from 'react-error-boundary'
import LocationProps from './props/LocationProps'
import { getDataFromCMS } from './helpers'
import './app.scss'
import CleanoutItemProps from './props/CleanoutItems'
import { transformCleanoutItemsToArray } from './helpers/transforms'
import { Filter } from './components/sidebar'
import CouncilProps from './props/CouncilProps'
import SearchSetProps from './props/SearchSetProps'
import { transformLocationsToArray } from './places'

const App = () => {
  const [mapKey, setMapKey] = useState<string | null>(null)
  const [mapId, setMapId] = useState<string | null>(null)
  const [sectionTitle] = useState<string>(config.view.pageTitle)
  const [activeView, setActiveView] = useState<string>(config.view.panelView)
  const [userLatLng, setUserLatLng] =useState<google.maps.LatLngLiteral | null>(null)
  const [userBounds, setUserBounds] = useState<google.maps.LatLngBounds | null | undefined>(undefined)
  const [userSetByGeo, setUserSetByGeo] = useState<boolean>(false)
  const [userSetBySearch, setUserSetBySearch] = useState<boolean>(false)
  const [userSetType, setUserSetType] = useState<SearchSetProps>({ type: null })
  const [userShowModal, setUserShowModal] = useState<boolean>(false)
  const [activeLocation, setActiveLocation] = useState<LocationProps[]>([])
  const [councilList, setCouncilList] = useState<CouncilProps>({})
  const [chemicalList, setChemicalList] = useState<CleanoutItemProps[]>([])
  const [goodsList, setGoodsList] = useState<CleanoutItemProps[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedCollectables, setSelectedCollectbles] = useState<string[]>([])
  const [appliedCategories, setAppliedCategories] = useState<string[]>([])
  const [appliedCollectables, setAppliedCollectbles] = useState<string[]>([])
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const [locations, setLocations] = useState<LocationProps[]>([])
  const [initialBounds, setInitialBounds] = useState<google.maps.LatLngBounds | undefined>(undefined)
  const [filterError, setFilterError] = useState<boolean>(false)
  const [modalTrigger, setModalTrigger] = useState<HTMLElement | null>(null)

  const filterLocations = useCallback(() => {
    const workLocations = locations
    workLocations.forEach(loc => {
      // Combine both taxonomies' terms for the location
      const locationTerms = [
        ...(loc.chemicals_and_liquids?.map(cal => cal.id) || []),
        ...(loc.household_items?.map(hhi => hhi.id) || [])
      ]

      // Check if location has all selected collectable terms
      const hasAllCollectables = appliedCollectables.length === 0 ||
        appliedCollectables.every(term => locationTerms.includes(term))

      const locHasCat = appliedCategories?.includes(loc.type)

      loc.active =
        (appliedCategories.length === 0 || locHasCat) &&
        hasAllCollectables
    })
    return workLocations
  }, [locations, appliedCategories, appliedCollectables])

  useEffect(() => {
    setLocations(() => [...filterLocations()])
  }, [appliedCategories, appliedCollectables])

  useEffect(() => {
    setAppliedCollectbles(() => {
      return [...selectedCollectables]
    })
  }, [selectedCollectables])

  useEffect(() => {
    setAppliedCategories(() => {
      return [...selectedCategories]
    })
  }, [selectedCategories])

  useEffect(() => {
    // error is true if length is false, so we need to switch it
    setFilterError(!locations.some(l => l.active))
  }, [locations])

  useEffect(() => {
    const key = getDataFromCMS('map-api-key')
    if (key) setMapKey(key)

    const id = getDataFromCMS('map-id')
    if (id) setMapId(id)

    const councils = getDataFromCMS('councils')
    if (councils) setCouncilList(JSON.parse(councils))

    const chemicals = getDataFromCMS('chemicals')
    if (chemicals) setChemicalList(transformCleanoutItemsToArray(chemicals))

    const goods = getDataFromCMS('household-items')
    if (goods) setGoodsList(transformCleanoutItemsToArray(goods))

    const crcData = getDataFromCMS('crc-pins')
    const creData = getDataFromCMS('cleanout-pins')
    let crcLocations: LocationProps[] = [],
      creLocations: LocationProps[] = []

    if (crcData) crcLocations = transformLocationsToArray(crcData, 'crc')
    if (creData) creLocations = transformLocationsToArray(creData, 'cre')
    setLocations(() => [...crcLocations, ...creLocations])
  }, [])

  return (
    <ErrorBoundary fallback={<div>Something went wrong</div>}>
      {mapKey !== null && (
        <APIProvider apiKey={mapKey}>
          <section className={`nsw-section ${config.namespace}-section`}>
            <div className="nsw-container">
              <div className="header">
                {/* <Header sectionTitle={sectionTitle} /> */}
                <Search
                  setUserLatLng={setUserLatLng}
                  setUserBounds={setUserBounds}
                  setUserSetType={setUserSetType}
                  setUserSetBySearch={setUserSetBySearch}
                  setUserSetByGeo={setUserSetByGeo}
                  initialBounds={initialBounds}
                />
              </div>
              <div className="nsw-layout nsw-m-top-md">
                <div
                  className="nsw-layout__sidebar"
                  aria-labelledby={`${config.namespace}-sidebartitle`}
                >
                  <Filter
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    selectedCollectables={selectedCollectables}
                    setSelectedCollectables={setSelectedCollectbles}
                    filtersApplied={filtersApplied}
                    setFiltersApplied={setFiltersApplied}
                    filterLists={[...goodsList, ...chemicalList]}
                    locations={locations}
                  />
                </div>
                <div className="nsw-layout__main">
                  <Switch
                    activeView={activeView}
                    setActiveView={setActiveView}
                  />
                  {filterError &&
                    <div className="nsw-in-page-alert nsw-in-page-alert--warning nsw-m-bottom-lg">
                      <span className="material-icons nsw-material-icons nsw-in-page-alert__icon" aria-hidden="true">error</span>
                      <div className="nsw-in-page-alert__content">
                        <p className="nsw-h5">No locations found.</p>
                        <p>It looks like you've selected a set of filters that match to no current locations. Please try a different set of filters.</p>
                      </div>
                    </div>
                  }
                  <div
                    className={`views-map ${activeView === 'map' ? 'nsw-display-block' : 'nsw-display-none'}`}
                    tabIndex={0}
                    hidden={activeView !== 'map'}
                  >
                    {mapId && (
                      <>
                        <ShowMap
                          locations={locations}
                          setUserShowModal={setUserShowModal}
                          setActiveLocation={setActiveLocation}
                          userLatLng={userLatLng}
                          userBounds={userBounds}
                          mapId={mapId}
                          userSetByGeo={userSetByGeo}
                          userSetBySearch={userSetBySearch}
                          userSetType={userSetType}
                          setInitialBounds={setInitialBounds}
                          setModalTrigger={setModalTrigger}
                          />

                      </>
                    )}
                  </div>
                  <div
                    className={`views-list ${activeView === 'list' ? 'nsw-display-block' : 'nsw-display-none'}`}
                    tabIndex={0}
                    hidden={activeView !== 'list'}
                  >
                    <ShowList
                      locations={locations}
                      setActiveLocation={setActiveLocation}
                      setUserShowModal={setUserShowModal}
                      userLatLng={userLatLng}
                      appliedCategories={appliedCategories}
                      appliedCollectables={appliedCollectables}
                      filtersApplied={filtersApplied}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Modal
            showModal={userShowModal}
            councilList={councilList}
            activeLocation={activeLocation}
            setUserShowModal={setUserShowModal}
            modalTrigger={modalTrigger}
          />
        </APIProvider>
      )}
    </ErrorBoundary>
  )
}

export default App
